import { Button } from '@/components/button'
import { Header } from '@/components/header'
import { API_HOST } from '@/constants/api'
import { cn } from '@/utils/cn'
import {
	Combobox,
	ComboboxInput,
	ComboboxOption,
	ComboboxOptions,
	Description,
	Dialog,
	DialogPanel,
	DialogTitle,
	Field,
	Fieldset,
	Input,
	Label,
	Select,
} from '@headlessui/react'
import debounce from 'lodash.debounce'
import { CheckIcon, ChevronDownIcon, XIcon } from 'lucide-react'
import {
	FormEvent,
	useCallback,
	useEffect,
	useEffectEvent,
	useState,
} from 'react'
import { toast } from 'sonner'

interface State {
	state: string
	count: number
}

interface Pagination {
	count: number
	limit: number
	page: number
	totalPages: number
}

interface StateCode {
	code: string
	description: string
	facility: string
	facility_rate: string
	id: number
	isMedicaid: string
	isMedicare: string
	non_facility_rate: string
	rate: string
	state: string
}

interface FormPayload {
	state: string
	codes: string[]
	N: number
	MH: number
	PR: number
	CMR: number
	D: number
	paidBy: string
	facilityType: string
}

interface Rate {
	state: string
	paidBy: string
	facilityType: string
	N: number
	MH: number
	PR: number
	CMR: number
	D: number
	codes: Array<{
		code: string
		description: string
		CR: string
		totalReimbursement: string
		dailyRevenue: string
		monthlyRevenue: string
		annualRevenue: string
	}>
	totals: {
		totalReimbursement: string
		dailyRevenue: string
		monthlyRevenue: string
		annualRevenue: string
		ROI: string
	}
}

function getLastValue(str: string) {
	if (!str) return ''

	const parts = str.split(',').map(s => s.trim())
	return parts[parts.length - 1]
}

function formatCurrency(value?: number | undefined) {
	if (value == null) return ''
	return value.toLocaleString('en-US', {
		style: 'currency',
		currency: 'USD',
	})
}

const disabledStates = [
	'AL',
	'AK',
	'AR',
	'CA',
	'CT',
	'DE',
	'FL',
	'GA',
	'HI',
	'ID',
	'IL',
	'IN',
	'IA',
	'KS',
	'KY',
	'LA',
	'ME',
	'MD',
	'MA',
	'MI',
	'MN',
	'MS',
	'MO',
	'MT',
	'NE',
	'NV',
	'NH',
	'NJ',
	'NM',
	'NY',
	'NC',
	'ND',
	'OK',
	'OR',
	'PA',
	'RI',
	'SC',
	'SD',
	'TN',
	'TX',
	'UT',
	'VT',
	'VA',
	'WA',
	'WV',
	'WI',
	'WY',
]

const Home = () => {
	const [states, setStates] = useState<string[]>([])
	const [codes, setCodes] = useState<StateCode[]>([])
	const [formValues, setFormValues] = useState<{
		N: string
		PR: string
		state: string
		codes: StateCode[]
		MH: string
		CMR: string
		D: string
		paidBy: string
		facilityType: string
	}>({
		N: '',
		PR: '',
		state: '',
		codes: [],
		MH: '',
		CMR: '',
		D: '',
		paidBy: '',
		facilityType: '',
	})
	const [currentPage, setCurrentPage] = useState(1)
	const [isOpen, setIsOpen] = useState(false)
	const [totalPages, setTotalPages] = useState<number>(0)
	const [query, setQuery] = useState('')
	const [calculatedRates, setCalculatedRates] = useState<Rate | null>(null)

	const getStates = useEffectEvent(async () => {
		try {
			const response = await fetch(API_HOST + '/api/healthcare-rates/states')
			const data: { data: State[] } = await response.json()
			setStates([...data.data.map(d => d.state), ...disabledStates])
		} catch (err: any) {
			console.log(err)
		}
	})

	const getStateCodes = async (
		state: string,
		pageNumber = 1,
		shouldAppend: boolean,
		search?: string,
	) => {
		try {
			const params = new URLSearchParams({
				state,
				page: pageNumber.toString(),
			})

			if (search) {
				const sanitizedSearch = getLastValue(search)
				params.append('search', sanitizedSearch)
			}

			const response = await fetch(
				API_HOST + `/api/healthcare-rates?${params.toString()}`,
			)

			const data: { data: StateCode[]; meta: Pagination } =
				await response.json()

			if (shouldAppend) {
				setCodes(prev => [...prev, ...data.data])
			} else {
				setCodes(data.data)
			}

			setTotalPages(data.meta.totalPages)
		} catch (err) {
			console.log(err)
		}
	}

	const getRates = async (data: FormPayload) => {
		try {
			const response = await fetch(
				`${API_HOST}/api/healthcare-rates/calculate`,
				{
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify(data),
				},
			)

			if (!response.ok) {
				let errorMsg = 'Failed to calculate rates'

				try {
					const errorData = await response.json()
					if (errorData?.error) errorMsg = errorData.error
					if (errorData?.message) errorMsg = errorData.message
				} catch {
					errorMsg = response.statusText || errorMsg
				}

				toast.error(errorMsg, { position: 'top-center' })
				return
			}

			const parsedResponse: { data: Rate } = await response.json()
			toast.success('Rates calculated', { position: 'top-center' })
			setCalculatedRates(parsedResponse.data)
		} catch (err: any) {
			const errorMsg = err?.message || 'Something went wrong'
			toast.error(errorMsg, { position: 'top-center' })
			console.error('getRates error:', err)
		}
	}

	useEffect(() => {
		getStates()
	}, [])

	const debouncedGetStateCodes = useCallback(
		debounce((state, page, flag, query) => {
			getStateCodes(state, page, flag, query)
		}, 800),
		[],
	)

	useEffect(() => {
		return () => {
			debouncedGetStateCodes.cancel()
		}
	}, [debouncedGetStateCodes])

	const onSubmit = (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault()

		const payload = {
			CMR: Number(formValues.CMR),
			D: Number(formValues.D),
			MH: Number(formValues.MH),
			N: Number(formValues.N),
			PR: Number(formValues.PR),
			codes: formValues.codes.map(c => c.code),
			facilityType: formValues.facilityType,
			paidBy: formValues.paidBy === 'tricare' ? 'medicare' : formValues.paidBy,
			state: formValues.state,
		}

		getRates(payload)
	}

	return (
		<main>
			<Header />
			<Dialog
				open={isOpen}
				as="div"
				className="relative z-10 focus:outline-none"
				onClose={() => setIsOpen(false)}>
				<div className="fixed inset-0 z-10 w-screen overflow-y-auto">
					<div className="flex min-h-full items-center justify-center p-4">
						<DialogPanel
							transition
							className="relative max-h-[450px] w-full max-w-2xl overflow-auto rounded-xl bg-white p-6 backdrop-blur-2xl duration-300 ease-out data-closed:transform-[scale(95%)] data-closed:opacity-0">
							<DialogTitle as="h3" className="text-xl font-bold text-black">
								Field Instructions
							</DialogTitle>
							<XIcon
								onClick={() => setIsOpen(false)}
								className="absolute top-4 right-4 size-6 cursor-pointer text-black"
							/>
							<ul className="mt-4 flex list-inside list-disc flex-col gap-4">
								<li>
									<span className="font-bold">
										Number of procedures per month?:{' '}
									</span>{' '}
									This field is to input the average number of medical
									procedures your practice performs each month.
								</li>
								<li>
									<span className="font-bold">
										% paid by Medicare/Medicaid?:{' '}
									</span>{' '}
									Enter the percentage of your practice&apos;s income that comes
									from Medicare and/or Medicaid reimbursements. It should be a
									number between 0 and 100.
								</li>{' '}
								<li>
									<span className="font-bold">State Code: </span> Select the
									state in which your practice operates.
								</li>{' '}
								<li>
									<span className="font-bold">Codes: </span> Enter the relevant
									medical billing codes (e.g., CPT, ICD-10) for the procedures
									you perform.
								</li>{' '}
								<li>
									<span className="font-bold">
										Minutes/Hours for Evaluation/Treatment?:{' '}
									</span>{' '}
									Input the average time spent (in minutes or hours) for patient
									evaluation and treatment.
								</li>{' '}
								<li>
									<span className="font-bold">
										Current Monthly Reimbursement Total?:{' '}
									</span>{' '}
									Enter the total amount of reimbursement your practice
									currently receives each month.
								</li>
								<li>
									<span className="font-bold">Days?: </span> Input the number of
									days you work.
								</li>
								<li>
									<span className="font-bold">Paid By?: </span> Select who is
									paying.
								</li>
							</ul>
							<p className="mt-5">
								After filling in the required information, you can submit the
								form to see a potential revenue estimate.
							</p>
							<p className="mt-5">
								Disclaimer:{' '}
								<span className="italic">
									&quot;Some states have not updated information on Facilities
									rates. Please confirm with your payor if separate rates
									apply.&quot;
								</span>
							</p>
						</DialogPanel>
					</div>
				</div>
			</Dialog>
			<section className="mx-auto my-14 flex w-11/12 flex-col lg:w-10/12">
				<h1 className="text-center text-4xl font-medium text-black">
					Estimate Income For Your Practice (ROI Calculator)
				</h1>
				<p className="text-primary mt-5 md:mt-10 md:text-lg">
					<span className="font-bold">Disclaimer: </span>The calculations are
					estimates. Please confirm reimbursement rates with your state or
					commercial insurance companies.
				</p>
				<form onSubmit={onSubmit} className="mt-5 flex flex-col gap-5 md:mt-10">
					<div className="flex items-center justify-between">
						<p className="text-base">
							&quot;<span className="text-primary">*</span>&quot; indicates
							required fields
						</p>
						<p
							onClick={() => setIsOpen(true)}
							className="hover:text-primary cursor-pointer hover:underline">
							Click here for field instructions
						</p>
					</div>
					<Fieldset className="grid grid-cols-1 items-start gap-5 lg:grid-cols-2">
						<Field>
							<Label className="text-base font-semibold text-black">
								Number of procedures per month? *
							</Label>
							<p className="text-sm font-medium">
								(This field is to input the average number of medical procedures
								your practice performs each month.)
							</p>
							<div className="relative">
								<Input
									name="N"
									value={formValues.N}
									onChange={e =>
										setFormValues(prev => ({ ...prev, N: e.target.value }))
									}
									required
									type="number"
									className="bg-secondary mt-2 block w-full rounded-[10px] border border-[#cecece] p-5 text-base font-bold text-white focus:not-data-focus:outline-none data-focus:outline-2 data-focus:-outline-offset-2 data-focus:outline-black"
								/>
								<XIcon
									onClick={() => setFormValues(prev => ({ ...prev, N: '' }))}
									className={cn(
										'absolute top-1/2 right-4 size-6 -translate-y-1/2 cursor-pointer text-white',
										formValues.N ? 'visible' : 'invisible',
									)}
								/>
							</div>
						</Field>
						<Field>
							<Label className="text-base font-semibold text-black">
								% Reimbursement paid by Medicaid, Medicare, Tri-Care or Private
								Insurer *
							</Label>
							<p className="text-sm font-medium">
								(Enter the percentage of your practice&apos;s income that comes
								from Medicare and/or Medicaid reimbursements. It should be a
								number between 0 and 100.)
							</p>
							<div className="relative">
								<Input
									name="PR"
									value={formValues.PR}
									onChange={e =>
										setFormValues(prev => ({ ...prev, PR: e.target.value }))
									}
									type="number"
									required
									className="bg-secondary mt-2 block w-full rounded-[10px] border border-[#cecece] p-5 text-base font-bold text-white focus:not-data-focus:outline-none data-focus:outline-2 data-focus:-outline-offset-2 data-focus:outline-black"
								/>
								<XIcon
									onClick={() => setFormValues(prev => ({ ...prev, PR: '' }))}
									className={cn(
										'absolute top-1/2 right-4 size-6 -translate-y-1/2 cursor-pointer text-white',
										formValues.PR ? 'visible' : 'invisible',
									)}
								/>
							</div>
							<Description className="mt-2 text-base text-black">
								Please enter a number from{' '}
								<span className="font-semibold">0</span> to{' '}
								<span className="font-semibold">100</span>.
							</Description>
						</Field>
						<Field>
							<Label className="text-base font-semibold text-black">
								State *
							</Label>
							<p className="text-sm font-medium">
								(Select the state in which your practice operates.)
							</p>
							<div className="relative">
								<Select
									required
									name="state"
									value={formValues.state}
									onChange={e => {
										setFormValues(prev => ({ ...prev, codes: [] }))
										setCurrentPage(1)
										setFormValues(prev => ({ ...prev, state: e.target.value }))
										getStateCodes(e.target.value, 1, false)
									}}
									className={cn(
										'bg-secondary mt-2 block w-full rounded-[10px] border border-[#cecece] p-5 text-base font-bold text-white',
										'focus:not-data-focus:outline-none data-focus:outline-2 data-focus:-outline-offset-2 data-focus:outline-black',
										'*:text-white',
									)}>
									<option value="" className="hidden"></option>
									{states.map(state => (
										<option
											disabled={disabledStates.includes(state)}
											key={state}
											value={state}>
											{state}
										</option>
									))}
								</Select>
								<XIcon
									onClick={() => {
										setFormValues(prev => ({ ...prev, state: '', codes: [] }))
										setCodes([])
										setCurrentPage(1)
									}}
									className={cn(
										'absolute top-1/2 right-7 size-6 -translate-y-1/2 cursor-pointer text-white',
										formValues.state ? 'visible' : 'invisible',
									)}
								/>
							</div>
							<ChevronDownIcon
								className="group pointer-events-none absolute top-2.5 right-2.5 size-4 fill-white"
								aria-hidden="true"
							/>
						</Field>
						<Field>
							<Label className="text-base font-semibold text-black">
								Reimbursement Codes *
							</Label>

							<p className="text-sm font-medium">
								(Enter the relevant medical billing codes (e.g., CPT, ICD-10)
								for the procedures you perform.)
							</p>
							<Combobox
								value={formValues.codes}
								virtual={
									currentPage < totalPages
										? {
												options: [
													...codes,
													{
														code: 'Load More...',
														description: '',
														facility: '',
														facility_rate: '',
														id: 0,
														isMedicaid: '',
														isMedicare: '',
														non_facility_rate: '',
														rate: '',
														state: '',
													},
												],
											}
										: { options: codes }
								}
								name="codes"
								immediate
								multiple
								onChange={value => {
									if (value.some(v => v.code === 'Load More...')) {
										getStateCodes(
											formValues.state,
											currentPage + 1,
											true,
											query,
										)
										return setCurrentPage(prev => prev + 1)
									}
									if (value.length > 5)
										return toast.error('Max 5 codes can be selected')
									setFormValues(prev => ({ ...prev, codes: value ?? [] }))
								}}
								onClose={() => {
									getStateCodes(formValues.state, 1, false)
									setQuery('')
								}}>
								<div className="relative">
									<ComboboxInput
										required
										autoComplete="off"
										aria-label="Code"
										className="bg-secondary mt-2 block w-full self-stretch rounded-[10px] border border-[#cecece] px-5 py-[18px] text-base font-bold text-white"
										displayValue={(code: StateCode[]) =>
											code?.map(c => c.code).join(', ')
										}
										onChange={event => {
											setCurrentPage(1)
											setQuery(event.target.value)
											debouncedGetStateCodes(
												formValues.state,
												1,
												false,
												event.target.value,
											)
										}}
									/>
									<XIcon
										onClick={() =>
											setFormValues(prev => ({ ...prev, codes: [] }))
										}
										className={cn(
											'absolute top-1/2 right-7 size-6 -translate-y-1/2 cursor-pointer text-white',
											formValues.codes.length > 0 ? 'visible' : 'invisible',
										)}
									/>
								</div>
								<ComboboxOptions
									anchor="bottom"
									transition
									className="bg-secondary w-(--input-width) origin-top rounded-[10px] border py-2 transition duration-200 ease-out empty:invisible data-closed:scale-95 data-closed:opacity-0">
									{({ option: code }) => (
										<ComboboxOption
											key={code.id}
											value={code}
											className="hover:text-primary group data-selected:text-primary flex w-full grow cursor-pointer items-center justify-between gap-2 px-5 py-1 text-white hover:bg-white data-selected:bg-white">
											<div className="flex grow flex-col leading-6">
												{code.code}
												{code.description && (
													<span className="text-xs">({code.description})</span>
												)}
											</div>
											<CheckIcon className="text-primary invisible size-4 shrink-0 group-data-selected:visible" />
										</ComboboxOption>
									)}
								</ComboboxOptions>
							</Combobox>
							<Description className="mt-2 text-base text-black">
								Users should select codes from the dropdown list or enter their
								preferred reimbursement code.
							</Description>
						</Field>
						<Field>
							<Label className="text-base font-semibold text-black">
								Minutes/Hours for Evaluation/Treatment? *
							</Label>
							<p className="text-sm font-medium">
								(Input the average time spent (in minutes or hours) for patient
								evaluation and treatment.)
							</p>
							<div className="relative">
								<Select
									required
									name="MH"
									value={formValues.MH}
									onChange={e => {
										setFormValues(prev => ({ ...prev, MH: e.target.value }))
									}}
									className={cn(
										'bg-secondary mt-2 block w-full rounded-[10px] border border-[#cecece] p-5 text-base font-bold text-white',
										'focus:not-data-focus:outline-none data-focus:outline-2 data-focus:-outline-offset-2 data-focus:outline-black',
										'*:text-white',
									)}>
									<option value="" className="hidden"></option>
									{Array.from({ length: 16 }, (_, i) => (i + 1) * 15).map(
										min => (
											<option key={min} value={min}>
												{min} minutes
											</option>
										),
									)}
								</Select>
								<XIcon
									onClick={() => {
										setFormValues(prev => ({ ...prev, MH: '' }))
									}}
									className={cn(
										'absolute top-1/2 right-7 size-6 -translate-y-1/2 cursor-pointer text-white',
										formValues.MH ? 'visible' : 'invisible',
									)}
								/>
							</div>
							<ChevronDownIcon
								className="group pointer-events-none absolute top-2.5 right-2.5 size-4 fill-white"
								aria-hidden="true"
							/>
						</Field>
						<Field>
							<Label className="text-base font-semibold text-black">
								Current Monthly Reimbursement Total? *
							</Label>
							<p className="text-sm font-medium">
								(Enter the total amount of reimbursement your practice currently
								receives each month.)
							</p>
							<div>
								<Input
									name="CMR"
									type="number"
									value={formValues.CMR}
									onChange={e =>
										setFormValues(prev => ({ ...prev, CMR: e.target.value }))
									}
									required
									className="bg-secondary mt-2 block w-full rounded-[10px] border border-[#cecece] p-5 text-base font-bold text-white focus:not-data-focus:outline-none data-focus:outline-2 data-focus:-outline-offset-2 data-focus:outline-black"
								/>
								<XIcon
									onClick={() => setFormValues(prev => ({ ...prev, CMR: '' }))}
									className={cn(
										'absolute top-1/2 right-4 size-6 -translate-y-1/2 cursor-pointer text-white',
										formValues.CMR ? 'visible' : 'invisible',
									)}
								/>
							</div>
							<Description className="mt-2 text-base text-black">
								Enter your average monthly reimbursement rates.
							</Description>
						</Field>
						<Field>
							<Label className="text-base font-semibold text-black">
								Days? *
							</Label>
							<p className="text-sm font-medium">
								(Input the number of days you work.)
							</p>
							<div className="relative">
								<Select
									required
									name="D"
									value={formValues.D}
									onChange={e => {
										setFormValues(prev => ({ ...prev, D: e.target.value }))
									}}
									className={cn(
										'bg-secondary mt-2 block w-full rounded-[10px] border border-[#cecece] p-5 text-base font-bold text-white',
										'focus:not-data-focus:outline-none data-focus:outline-2 data-focus:-outline-offset-2 data-focus:outline-black',
										'*:text-white',
									)}>
									<option value="" className="hidden"></option>
									{Array.from({ length: 29 }, (_, i) => i + 1).map(day => (
										<option key={day} value={day}>
											{day} day{day === 1 ? '' : 's'}
										</option>
									))}
								</Select>
								<XIcon
									onClick={() => {
										setFormValues(prev => ({ ...prev, D: '' }))
									}}
									className={cn(
										'absolute top-1/2 right-7 size-6 -translate-y-1/2 cursor-pointer text-white',
										formValues.D ? 'visible' : 'invisible',
									)}
								/>
							</div>
							<ChevronDownIcon
								className="group pointer-events-none absolute top-2.5 right-2.5 size-4 fill-white"
								aria-hidden="true"
							/>
						</Field>
						<Field>
							<Label className="text-base font-semibold text-black">
								Paid By? *
							</Label>
							<p className="text-sm font-medium">(Select who is paying.)</p>
							<div className="relative">
								<Select
									required
									name="paidBy"
									value={formValues.paidBy}
									onChange={e =>
										setFormValues(prev => ({ ...prev, paidBy: e.target.value }))
									}
									className={cn(
										'bg-secondary mt-2 block w-full rounded-[10px] border border-[#cecece] p-5 text-base font-bold text-white',
										'focus:not-data-focus:outline-none data-focus:outline-2 data-focus:-outline-offset-2 data-focus:outline-black',
										'*:text-white',
									)}>
									<option value="" className="hidden"></option>
									<option value="medicaid">Medicaid</option>
									<option value="medicare">Medicare</option>
									<option value="tricare">Tri-Care</option>
									<option value="aetna">Aetna</option>
									<option value="blueCrossBlueShield">
										Blue Cross Blue Shield
									</option>
									<option value="unitedHealthcare">United Healthcare</option>
									<option value="cigna">Cigna</option>
									<option value="other">Other</option>
								</Select>

								<XIcon
									onClick={() =>
										setFormValues(prev => ({ ...prev, paidBy: '' }))
									}
									className={cn(
										'absolute top-1/2 right-7 size-6 -translate-y-1/2 cursor-pointer text-white',
										formValues.paidBy ? 'visible' : 'invisible',
									)}
								/>
							</div>
							<ChevronDownIcon
								className="group pointer-events-none absolute top-2.5 right-2.5 size-4 fill-white"
								aria-hidden="true"
							/>
						</Field>
						<Field>
							<Label className="text-base font-semibold text-black">
								Facilities *
							</Label>
							<div className="relative">
								<Select
									required
									name="facilityType"
									value={formValues.facilityType}
									onChange={e =>
										setFormValues(prev => ({
											...prev,
											facilityType: e.target.value,
										}))
									}
									className={cn(
										'bg-secondary mt-2 block w-full rounded-[10px] border border-[#cecece] p-5 text-base font-bold text-white',
										'focus:not-data-focus:outline-none data-focus:outline-2 data-focus:-outline-offset-2 data-focus:outline-black',
										'*:text-white',
									)}>
									<option value="" className="hidden"></option>
									<option value="unknown/NA">unknown/NA</option>
									<option value="Yes">Yes</option>
									<option value="No">No</option>
								</Select>
								<XIcon
									onClick={() =>
										setFormValues(prev => ({ ...prev, facilityType: '' }))
									}
									className={cn(
										'absolute top-1/2 right-7 size-6 -translate-y-1/2 cursor-pointer text-white',
										formValues.facilityType ? 'visible' : 'invisible',
									)}
								/>
							</div>
							<ChevronDownIcon
								className="group pointer-events-none absolute top-2.5 right-2.5 size-4 fill-white"
								aria-hidden="true"
							/>
							<Description className="mt-2 text-base text-black">
								Some states have not updated information on Facilities rates.
								Please confirm with your payor if separate rates apply.
							</Description>
						</Field>
					</Fieldset>
					<Button className="self-end">Submit</Button>
				</form>

				<div className="mt-10 flex flex-col gap-5">
					<div className="flex items-center justify-between border-b border-[#cccccc] pb-4">
						<h2 className="text-2xl font-medium text-black">
							Estimated Revenue
						</h2>
						{calculatedRates && (
							<Button
								className="px-4 text-base"
								onClick={() => setCalculatedRates(null)}>
								Reset
							</Button>
						)}
					</div>
					<table className="table-fixed border-separate border-spacing-y-1">
						<thead>
							<tr>
								<th className="font-bold text-black">Total Reimbursement</th>
								<th className="font-bold text-black">Daily Revenue</th>
								<th className="font-bold text-black">Monthly Revenue</th>
								<th className="font-bold text-black">Annual Revenue</th>
								<th className="font-bold text-black">ROI</th>
							</tr>
						</thead>
						<tbody>
							<tr>
								<td align="center" className="text-secondary p-5 font-semibold">
									{formatCurrency(
										calculatedRates?.totals.totalReimbursement
											? Number(calculatedRates?.totals.totalReimbursement)
											: undefined,
									)}
								</td>
								<td align="center" className="text-secondary p-5 font-semibold">
									{formatCurrency(
										calculatedRates?.totals.dailyRevenue
											? Number(calculatedRates?.totals.dailyRevenue)
											: undefined,
									)}
								</td>
								<td align="center" className="text-secondary p-5 font-semibold">
									{formatCurrency(
										calculatedRates?.totals.monthlyRevenue
											? Number(calculatedRates?.totals.monthlyRevenue)
											: undefined,
									)}
								</td>
								<td align="center" className="text-secondary p-5 font-semibold">
									{formatCurrency(
										calculatedRates?.totals.annualRevenue
											? Number(calculatedRates?.totals.annualRevenue)
											: undefined,
									)}
								</td>
								<td align="center" className="text-secondary p-5 font-semibold">
									{formatCurrency(
										calculatedRates?.totals.ROI
											? Number(calculatedRates?.totals.ROI)
											: undefined,
									)}
								</td>
							</tr>
						</tbody>
					</table>
				</div>
			</section>
			<footer className="mx-auto w-11/12 pb-5 lg:w-10/12">
				<p className="text-primary mb-5 md:mb-10 md:text-lg">
					<span className="font-bold">Disclaimer: </span>The calculations are
					estimates. Please confirm reimbursement rates with your state or
					commercial insurance companies.
				</p>
				<p className="text-left text-sm">
					Copyright © {new Date().getFullYear()} Keys Solutions LLC. All rights
					reserved worldwide. This website software design, including but not
					limited to the code, graphics, user interface, and documentation, is
					protected by United States copyright law and international treaties.
					Unauthorized reproduction, distribution, or modification of this work
					is prohibited.
				</p>
			</footer>
		</main>
	)
}

export default Home
